"use client";

import { useEffect } from "react";
import { m, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { createPortal } from "react-dom";
import type { TimelineEvent } from "./types";

interface TimelineModalProps {
    item: TimelineEvent | null;
    onClose: () => void;
}

export default function TimelineModal({ item, onClose }: TimelineModalProps) {
    useEffect(() => {
        const handleKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose();
        };

        if (item) {
            document.body.style.overflow = "hidden";
            document.addEventListener("keydown", handleKey);
        } else {
            document.body.style.overflow = "auto";
        }

        return () => {
            document.body.style.overflow = "auto";
            document.removeEventListener("keydown", handleKey);
        };
    }, [item, onClose]);

    if (!item) return null;

    return createPortal(
        <AnimatePresence>
            <m.div
                key="modal-root"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="fixed inset-0 z-[999999] flex items-center justify-center p-4 md:p-8 bg-black/60"
                onClick={onClose}
            >
                <m.div
                    initial={{ opacity: 0, scale: 0.95, y: 24 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: 24 }}
                    transition={{ type: "spring", stiffness: 320, damping: 28 }}
                    onClick={(e) => e.stopPropagation()}
                    className="relative w-full max-w-[740px] max-h-[92vh] bg-white rounded-[12px] shadow-2xl flex flex-col "
                >
                    <button
                        onClick={onClose}
                        aria-label="Close"
                        className="absolute -top-4 -right-4 z-20 w-10 h-10 rounded-full bg-[#3B82F6] flex items-center justify-center shadow-lg border-2 border-white hover:bg-[#2563eb] transition"
                    >
                        <X className="w-5 h-5 text-white" />
                    </button>

                    {item.image && (
                        <div className="w-full h-[200px] md:h-[500px] overflow-hidden flex-shrink-0">
                            <img
                                src={item.image}
                                alt={item.title}
                                className="w-full h-full object-cover rounded-t-[10px]"
                            />
                        </div>
                    )}

                    <div className="px-6 py-7 md:px-8 md:py-7 overflow-y-auto">
                        <h2 className="text-[#0B1B3A] font-bold text-[20px] md:text-[22px] leading-[130%] mb-2 font-NeueMontreal">
                            {item.title}
                        </h2>
                        <div className="text-[#4B5263] font-Schibsted_Grotesk text-[15px] leading-[175%] whitespace-pre-line md:line-clamp-none line-clamp-10">
                            {item.description}
                        </div>
                    </div>
                </m.div>
            </m.div>
        </AnimatePresence>,
        document.body
    );
}